import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { io } from "socket.io-client";
import { FiBell, FiUserPlus, FiUsers, FiTool, FiMessageSquare, FiCheckCircle, FiEdit3 } from "react-icons/fi";
import { GrShieldSecurity } from "react-icons/gr";
import { GiMagicBroom } from "react-icons/gi";
import notification from "../assets/sounds/notification.mp3";
import axiosInstance from "../utils/api";

const API_BASE = "https://api.expo-pilot.com";

const SOURCE_LABEL = {
    technique: "Technical",
    cleaning: "Cleaning",
    security: "HSE",
};

const SOURCE_ICON_CONFIG = {
    cleaning: {
        icon: GiMagicBroom,
        color: "bg-gradient-to-br from-cyan-500 to-cyan-600",
        dot: "bg-cyan-500",
    },
    security: {
        icon: GrShieldSecurity,
        color: "bg-gradient-to-br from-yellow-500 to-yellow-600",
        dot: "bg-yellow-500",
    },
};

const getDestination = (eventId, n) => {
    if (n.category === "ticket") {
        if (n.source === "cleaning") return `/event/${eventId}/cleaning`;
        if (n.source === "security") return `/event/${eventId}/security`;
        return `/event/${eventId}/cleaning`;
    }
    return `/event/${eventId}/badging`;
};

const TICKET_TYPE_CONFIG = {
    created: {
        icon: FiTool,
        color: "bg-gradient-to-br from-orange-500 to-orange-600",
        dot: "bg-orange-500",
        label: (d) => `New ${SOURCE_LABEL[d.source] || d.source} ticket opened for stand ${d.stand} by ${d.createdBy}`,
    },
    message: {
        icon: FiMessageSquare,
        color: "bg-gradient-to-br from-violet-500 to-violet-600",
        dot: "bg-violet-500",
        label: (d) => `New message on ${SOURCE_LABEL[d.source] || d.source} ticket (stand ${d.stand}) from ${d.sentBy}`,
    },
    closed: {
        icon: FiCheckCircle,
        color: "bg-gradient-to-br from-emerald-500 to-emerald-600",
        dot: "bg-emerald-500",
        label: (d) => `${SOURCE_LABEL[d.source] || d.source} ticket for stand ${d.stand} closed by ${d.closedBy}`,
    },
};

const playNotificationSound = () => {
    try {
        const audio = new Audio(notification);
        audio.volume = 0.5;
        audio.play().catch(() => { });
    } catch (_) { }
};

const fetchNotificationsFromServer = async (eventId) => {
    try {
        const res = await axiosInstance.get(`/notifications/${eventId}`);
        return res.data.map((n) => ({ ...n, time: new Date(n.time) }));
    } catch (err) {
        if (err.response?.status === 401 || err.response?.status === 403) {
            return [];
        }
        try {
            const saved = JSON.parse(
                localStorage.getItem(`notifications_event_${eventId}`) || "[]"
            );
            return saved.map((n) => ({ ...n, time: new Date(n.time) }));
        } catch {
            return [];
        }
    }
};

const saveNotificationToServer = async (eventId, notif) => {
    try {
        await axiosInstance.post(`/notifications/${eventId}`, notif);
    } catch { }
};

const markReadOnServer = async (eventId, notifId) => {
    try {
        await axiosInstance.patch(`/notifications/${eventId}/${notifId}/read`);
    } catch { }
};

const clearNotificationsOnServer = async (eventId) => {
    try {
        await axiosInstance.delete(`/notifications/${eventId}`);
    } catch { }
};


const Navbar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id: eventId } = useParams();

    const [notifications, setNotifications] = useState([]);
    const [open, setOpen] = useState(false);
    const panelRef = useRef(null);

    useEffect(() => {
        if (!eventId) return;
        fetchNotificationsFromServer(eventId).then((serverNotifs) => {
            if (serverNotifs) {
                setNotifications(serverNotifs);
            } else {
                try {
                    const saved = JSON.parse(localStorage.getItem(`notifications_event_${eventId}`) || "[]");
                    setNotifications(saved.map((n) => ({ ...n, time: new Date(n.time) })));
                } catch { setNotifications([]); }
            }
        });
    }, [eventId]);

    useEffect(() => {
        if (!eventId) return;
        localStorage.setItem(`notifications_event_${eventId}`, JSON.stringify(notifications));
    }, [notifications, eventId]);

    const pushNotification = useCallback((notif) => {
        playNotificationSound();
        setNotifications((prev) => [notif, ...prev.slice(0, 99)]);
        saveNotificationToServer(eventId, notif);
    }, [eventId]);

    useEffect(() => {
        if (!eventId) return;
        const socket = io(API_BASE);

        socket.on("new-registration", (data) => {
            if (data.eventId && data.eventId !== eventId) return;
            const message =
                data.type === "standiste"
                    ? `New stand builder registered: ${data.name}`
                    : `${data.created} new staff badge${data.created > 1 ? "s" : ""} submitted by ${data.standisteName}`;

            pushNotification({
                id: Date.now(),
                message,
                category: "registration",
                type: data.type,
                time: new Date(),
                read: false,
            });
        });

        socket.on("ticket-event", (data) => {
            if (data.eventId && data.eventId !== eventId) return;
            const config = TICKET_TYPE_CONFIG[data.type];
            if (!config) return;

            pushNotification({
                id: Date.now(),
                message: config.label(data),
                category: "ticket",
                type: data.type,
                source: data.source,
                ticketId: data.ticketId,
                time: new Date(),
                read: false,
            });
        });

        socket.on("badge-updated", (data) => {
            if (data.eventId && data.eventId !== eventId) return;

            pushNotification({
                id: Date.now(),
                message: `Badge "${data.badgeName}" was edited by chef ${data.chefName}`,
                category: "badge",
                type: "edit",
                badgeId: data.badgeId,
                time: new Date(),
                read: false,
            });
        });

        return () => socket.disconnect();
    }, [eventId, pushNotification]);

    useEffect(() => {
        const handler = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const unreadCount = notifications.filter((n) => !n.read).length;

    const clearAll = () => {
        setNotifications([]);
        clearNotificationsOnServer(eventId);
        setOpen(false);
    };

    const timeLabel = (date) => {
        const diff = Math.floor((Date.now() - new Date(date)) / 1000);
        if (diff < 60) return "just now";
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        return new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric" });
    };

    const handleNotificationClick = (n) => {

        setNotifications((prev) =>
            prev.map((notif) => notif.id === n.id ? { ...notif, read: true } : notif)
        );
        markReadOnServer(eventId, n.id);
        setOpen(false);

        const destination = getDestination(eventId, n);

        if (location.pathname === destination) {
            window.location.reload();
        } else {
            navigate(destination);
        }
    };

    const getNotifStyle = (n) => {
        if (n.category === "ticket") {
            const sourceOverride = SOURCE_ICON_CONFIG[n.source];
            const typeConfig = TICKET_TYPE_CONFIG[n.type] || TICKET_TYPE_CONFIG.created;
            return {
                Icon: sourceOverride?.icon ?? typeConfig.icon,
                color: sourceOverride?.color ?? typeConfig.color,
                dot: sourceOverride?.dot ?? typeConfig.dot,
                cta: n.source === "cleaning" ? "Go to Cleaning →"
                    : n.source === "security" ? "Go to HSE →"
                        : "Go to Tickets →",
            };
        }
        if (n.category === "badge") {
            return {
                Icon: FiEdit3,
                color: "bg-gradient-to-br from-indigo-500 to-indigo-600",
                dot: "bg-indigo-500",
                cta: "Go to Badging →",
            };
        }
        return {
            Icon: n.type === "standiste" ? FiUserPlus : FiUsers,
            color: n.type === "standiste"
                ? "bg-gradient-to-br from-blue-500 to-blue-600"
                : "bg-gradient-to-br from-emerald-500 to-emerald-600",
            dot: n.type === "standiste" ? "bg-blue-500" : "bg-emerald-500",
            cta: "Go to Badging →",
        };
    };

    return (
        <div className="relative" ref={panelRef}>
            {/* Bell button */}
            <button
                onClick={() => setOpen((o) => !o)}
                className="relative flex items-center justify-center w-10 h-10 rounded-2xl transition-all duration-200 group"
                style={{
                    background: unreadCount > 0 ? "linear-gradient(135deg, #eff6ff, #dbeafe)" : "#f8fafc",
                    border: unreadCount > 0 ? "1.5px solid #bfdbfe" : "1.5px solid #e2e8f0",
                }}
            >
                <FiBell size={17} className={`transition-colors ${unreadCount > 0 ? "text-blue-600" : "text-slate-400 group-hover:text-slate-600"}`} />
                {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 flex h-4 w-4">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-60" />
                        <span className="relative inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-500 text-white text-[8px] font-bold shadow">
                            {unreadCount > 9 ? "9+" : unreadCount}
                        </span>
                    </span>
                )}
            </button>

            {/* Dropdown panel */}
            {open && (
                <div
                    className="absolute right-0 mt-3 w-96 rounded-2xl overflow-hidden"
                    style={{
                        background: "#ffffff",
                        border: "1px solid #e8edf3",
                        boxShadow: "0 20px 60px -10px rgba(0,0,0,0.15), 0 4px 20px -4px rgba(0,0,0,0.08)",
                    }}
                >
                    {/* Header */}
                    <div className="px-5 py-4 flex items-center justify-between"
                        style={{ background: "linear-gradient(135deg, #0d2b5c 0%, #1a3a6e 100%)" }}>
                        <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 rounded-xl bg-white/15 flex items-center justify-center">
                                <FiBell size={13} className="text-white" />
                            </div>
                            <div>
                                <p className="text-sm font-bold text-white leading-tight">Notifications</p>
                                <p className="text-[10px] text-blue-200 mt-0.5">
                                    {unreadCount > 0 ? `${unreadCount} unread` : "All caught up"}
                                </p>
                            </div>
                        </div>
                        {notifications.length > 0 && (
                            <button
                                onClick={clearAll}
                                className="px-2 py-1 rounded-lg bg-white/10 hover:bg-red-500/40 text-white text-[10px] font-medium transition"
                            >
                                Clear all
                            </button>
                        )}
                    </div>

                    {/* List */}
                    <div className="max-h-80 overflow-y-auto">
                        {notifications.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-14 text-slate-400">
                                <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-3">
                                    <FiBell size={22} className="opacity-40" />
                                </div>
                                <p className="text-sm font-semibold text-slate-500">No notifications</p>
                                <p className="text-xs text-slate-400 mt-1">Tickets and registrations will appear here</p>
                            </div>
                        ) : (
                            <div className="divide-y divide-slate-50">
                                {notifications.map((n) => {
                                    const { Icon, color, dot, cta } = getNotifStyle(n);
                                    return (
                                        <div
                                            key={n.id}
                                            onClick={() => handleNotificationClick(n)}
                                            className={`flex gap-3 px-5 py-3.5 transition-colors cursor-pointer ${!n.read ? "bg-blue-50/60 hover:bg-blue-100/60" : "hover:bg-slate-50"}`}
                                        >
                                            <div className={`flex-shrink-0 w-9 h-9 rounded-2xl flex items-center justify-center shadow-sm text-white ${color}`}>
                                                <Icon size={14} />
                                            </div>
                                            <div className="flex-1 min-w-0 pt-0.5">
                                                <p className="text-xs text-slate-700 font-medium leading-snug">{n.message}</p>
                                                <div className="flex items-center gap-1.5 mt-1">
                                                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${dot}`} />
                                                    <p className="text-[10px] text-slate-400">{timeLabel(n.time)}</p>
                                                    <span className="text-[10px] text-slate-300">·</span>
                                                    <span className="text-[10px] text-blue-500 font-medium">{cta}</span>
                                                </div>
                                            </div>
                                            {!n.read && (
                                                <div className="flex-shrink-0 w-2 h-2 rounded-full bg-blue-500 mt-2 shadow-sm shadow-blue-300" />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    {notifications.length > 0 && (
                        <div className="px-5 py-2.5 border-t border-slate-100 bg-slate-50/80">
                            <p className="text-[10px] text-slate-400 text-center font-medium">
                                Showing {notifications.length} notification{notifications.length > 1 ? "s" : ""}
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Navbar;